import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { 
  Activity, ArrowRight, Loader2, CheckCircle, 
  Zap, BarChart3, LogOut,
  Edit2, Save, Eye, EyeOff, X, Copy, Link2, ExternalLink
} from "lucide-react";
import { apiClient } from "../../App";
import ConnectZerodha from "../auth/ConnectZerodha";
import { toast } from "sonner";

const BrokerSelector = ({ user, onConnected, onLogout, forceSelection = false }) => {
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brokerStatus, setBrokerStatus] = useState({
    zerodha: { has_credentials: false, connected: false }
  });
  const [skipAutoConnect, setSkipAutoConnect] = useState(forceSelection);
  
  // Credential editing states
  const [editingBroker, setEditingBroker] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  
  // Zerodha credentials (simplified - just API Key and Secret)
  const [zerodhaCredentials, setZerodhaCredentials] = useState({
    api_key: '',
    api_secret: ''
  });
  
  // Telegram credentials for alerts
  const [telegramCredentials, setTelegramCredentials] = useState({
    bot_token: '',
    chat_id: ''
  });
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  
  // Generate correct redirect URL for Zerodha - MUST match Kite Developer App settings
  const redirectUrl = `${window.location.origin}/api/auth/zerodha/callback`;

  useEffect(() => {
    checkBrokerStatuses();
    loadTelegramSettings();
  }, []);

  const loadTelegramSettings = async () => {
    try {
      const res = await apiClient.get('/user/telegram-settings');
      if (res.data) {
        setTelegramCredentials({
          bot_token: res.data.bot_token || '',
          chat_id: res.data.chat_id || ''
        });
        setTelegramEnabled(res.data.enabled || false);
      }
    } catch (err) {
      console.error('Failed to load telegram settings:', err);
    }
  };

  const checkBrokerStatuses = async () => {
    setLoading(true);
    try {
      const kiteRes = await apiClient.get('/user/kite/status');

      const newStatus = {
        zerodha: { has_credentials: false, connected: false }
      };

      if (kiteRes.data) {
        newStatus.zerodha = {
          has_credentials: kiteRes.data.has_credentials || false,
          connected: kiteRes.data.connected && !kiteRes.data.needs_daily_auth,
          needs_auth: kiteRes.data.needs_daily_auth || false
        };
      }

      setBrokerStatus(newStatus);

      // Auto-connect to Zerodha if connected
      if (!skipAutoConnect && !forceSelection && newStatus.zerodha.connected) {
        localStorage.setItem('active_broker', 'zerodha');
        onConnected && onConnected({ broker: 'zerodha', ...kiteRes.data });
      }
    } catch (err) {
      console.error('Failed to check broker statuses:', err);
    }
    setLoading(false);
  };

  const handleBrokerSelect = (broker) => {
    if (editingBroker === broker) {
      setEditingBroker(null);
    } else {
      setSelectedBroker(broker);
    }
  };

  const handleEditCredentials = (broker, e) => {
    e.stopPropagation();
    setEditingBroker(editingBroker === broker ? null : broker);
    setSelectedBroker(null);
  };

  const handleBrokerConnected = async (data) => {
    // Save active broker preference to backend
    try {
      await apiClient.post('/user/set-active-broker', { broker: 'zerodha' });
    } catch (err) {
      console.error('Failed to save active broker:', err);
    }
    
    localStorage.setItem('active_broker', 'zerodha');
    onConnected && onConnected({ 
      broker: 'zerodha',
      ...data 
    });
  };

  const handleBackToSelection = () => {
    setSelectedBroker(null);
    setSkipAutoConnect(true);
    checkBrokerStatuses();
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const copyToClipboard = (text, label) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(`${label} copied!`))
        .catch(() => fallbackCopyToClipboard(text, label));
    } else {
      fallbackCopyToClipboard(text, label);
    }
  };

  const fallbackCopyToClipboard = (text, label) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success(`${label} copied!`);
    } catch (err) {
      toast.info(`Please copy manually: ${text}`);
    }
    document.body.removeChild(textArea);
  };

  const saveZerodhaCredentials = async () => {
    if (!zerodhaCredentials.api_key || !zerodhaCredentials.api_secret) {
      toast.error('API Key and API Secret are required');
      return false;
    }
    
    setSaving(true);
    try {
      const response = await apiClient.post('/user/kite/save-credentials', {
        api_key: zerodhaCredentials.api_key,
        api_secret: zerodhaCredentials.api_secret,
        redirect_url: redirectUrl
      });
      if (response.data.success) {
        toast.success('Zerodha credentials saved!');
        await checkBrokerStatuses();
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    }
    setSaving(false);
    return false;
  };

  const connectToZerodha = async () => {
    // First save credentials if they're entered
    if (zerodhaCredentials.api_key && zerodhaCredentials.api_secret) {
      const saved = await saveZerodhaCredentials();
      if (!saved) return;
    }
    
    // Save telegram settings if enabled
    if (telegramEnabled && telegramCredentials.bot_token && telegramCredentials.chat_id) {
      try {
        await apiClient.post('/user/telegram-settings', {
          enabled: true,
          bot_token: telegramCredentials.bot_token,
          chat_id: telegramCredentials.chat_id
        });
        toast.success('Telegram alerts enabled!');
      } catch (error) {
        console.error('Failed to save telegram settings:', error);
      }
    }
    
    setConnecting(true);
    try {
      const response = await apiClient.get('/user/kite/login-url');
      if (response.data.success) {
        localStorage.setItem('kite_oauth_state', response.data.state);
        localStorage.setItem('active_broker', 'zerodha');
        window.location.href = response.data.login_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to get login URL';
      toast.error(message);
      if (message.includes('credentials')) {
        setEditingBroker('zerodha');
      }
    }
    setConnecting(false);
  };

  const connectWithCredentials = async (broker) => {
    if (broker === 'zerodha') {
      await connectToZerodha();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (selectedBroker === 'zerodha') {
    return (
      <ConnectZerodha 
        user={user}
        onConnected={handleBrokerConnected}
        onLogout={handleBackToSelection}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#030508] flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-500/10 via-blue-600/10 to-orange-500/10 rounded-full blur-[100px]" />
      </div>
      
      <div className="relative z-10 max-w-md w-full px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-gradient-to-br from-[#0a1628] to-[#061020] border-2 border-cyan-400/50 flex items-center justify-center">
            <Activity className="w-6 h-6 text-cyan-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1">Connect Your Broker</h1>
          <p className="text-xs text-[#6b7a99]">
            Connect to Zerodha for real-time market data • <span className="text-[#4a5a7a]">{user?.email}</span>
          </p>
        </div>
        
        {/* Zerodha Card */}
        <div 
          className={`bg-gradient-to-br from-[#0a1628] to-[#061020] border rounded-xl p-4 transition-all mb-4 ${
            editingBroker === 'zerodha' ? 'ring-2 ring-orange-500/50' :
            brokerStatus.zerodha.connected 
              ? 'border-green-500/50 ring-2 ring-green-500/20' 
              : brokerStatus.zerodha.has_credentials
                ? 'border-orange-500/50'
                : 'border-[#1a2540] hover:border-cyan-500/50'
          }`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center cursor-pointer"
                 onClick={() => handleBrokerSelect('zerodha')}>
              <Zap className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex items-center gap-2">
              {brokerStatus.zerodha.connected && (
                <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle className="w-2.5 h-2.5" /> Connected
                </span>
              )}
              {brokerStatus.zerodha.has_credentials && !brokerStatus.zerodha.connected && (
                <span className="text-[10px] bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded-full">
                  Login Required
                </span>
              )}
              <button 
                onClick={(e) => handleEditCredentials('zerodha', e)}
                className="p-1 rounded hover:bg-orange-500/20 text-orange-400"
                title="Edit Credentials"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          <h3 className="text-base font-bold text-white mb-1 cursor-pointer" onClick={() => handleBrokerSelect('zerodha')}>
            Zerodha Kite
          </h3>
          <p className="text-[10px] text-[#6b7a99] mb-3">India's largest broker</p>
          
          {/* Credential Form for Zerodha */}
          {editingBroker === 'zerodha' ? (
            <div className="space-y-3 mt-3 p-3 bg-[#0a0e14] rounded-lg border border-orange-500/30">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-orange-400">Zerodha Kite Connect Setup</h4>
                <button onClick={() => setEditingBroker(null)} className="text-[#6b7a99] hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Step 1: Redirect URL - MOST IMPORTANT */}
              <div className="p-2.5 bg-amber-500/10 border border-amber-500/40 rounded-lg">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-[10px] font-semibold text-amber-400">Step 1: Copy Redirect URL</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(redirectUrl, 'Redirect URL')}
                    className="flex items-center gap-1 text-[9px] text-amber-400 hover:text-amber-300 px-1.5 py-0.5 rounded bg-amber-500/20"
                  >
                    <Copy className="w-2.5 h-2.5" />
                    Copy
                  </button>
                </div>
                <div className="bg-[#030508] rounded px-2 py-1.5 font-mono text-[10px] text-white break-all select-all">
                  {redirectUrl}
                </div>
                <p className="text-[8px] text-amber-300/80 mt-1.5">
                  Paste this URL in your Kite Connect app's "Redirect URL" field at{" "}
                  <a href="https://developers.kite.trade/apps" target="_blank" rel="noreferrer" className="underline">
                    developers.kite.trade
                  </a>
                </p>
              </div>
              
              {/* Step 2: API Credentials */}
              <div className="space-y-2">
                <p className="text-[10px] text-[#6b7a99] flex items-center gap-1">
                  <span className="text-orange-400 font-semibold">Step 2:</span> Enter your API credentials
                </p>
                <div>
                  <label className="text-[9px] text-[#6b7a99] block mb-1">API Key *</label>
                  <Input 
                    type="text"
                    placeholder="Your Kite Connect API Key"
                    value={zerodhaCredentials.api_key}
                    onChange={(e) => setZerodhaCredentials(prev => ({...prev, api_key: e.target.value}))}
                    className="h-8 text-xs bg-[#030508] border-[#1a2540] focus:border-orange-500"
                    data-testid="zerodha-api-key-input"
                  />
                </div>
                <div>
                  <label className="text-[9px] text-[#6b7a99] block mb-1">API Secret *</label>
                  <div className="relative">
                    <Input 
                      type={showPasswords.api_secret ? "text" : "password"}
                      placeholder="Your Kite Connect API Secret"
                      value={zerodhaCredentials.api_secret}
                      onChange={(e) => setZerodhaCredentials(prev => ({...prev, api_secret: e.target.value}))}
                      className="h-8 text-xs bg-[#030508] border-[#1a2540] focus:border-orange-500 pr-8"
                      data-testid="zerodha-api-secret-input"
                    />
                    <button 
                      type="button"
                      onClick={() => togglePasswordVisibility('api_secret')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7a99] hover:text-white"
                    >
                      {showPasswords.api_secret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Telegram Alerts Section */}
              <div className="pt-3 border-t border-[#1a2540]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] text-purple-400 font-semibold flex items-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.36-.49.99-.75 3.88-1.69 6.47-2.8 7.77-3.35 3.7-1.54 4.47-1.81 4.97-1.82.11 0 .35.03.51.18.13.12.17.28.18.45-.01.06.01.24 0 .38z"/></svg>
                    Telegram Alerts (Optional)
                  </p>
                  <button
                    onClick={() => setTelegramEnabled(!telegramEnabled)}
                    className={`w-8 h-4 rounded-full transition-colors ${telegramEnabled ? 'bg-purple-500' : 'bg-[#1a2540]'}`}
                  >
                    <div className={`w-3 h-3 rounded-full bg-white transition-transform mx-0.5 ${telegramEnabled ? 'translate-x-3.5' : ''}`} />
                  </button>
                </div>
                {telegramEnabled && (
                  <div className="space-y-2 animate-in fade-in duration-200">
                    <div>
                      <label className="text-[9px] text-[#6b7a99] block mb-1">Bot Token</label>
                      <Input 
                        type="text"
                        placeholder="Your Telegram Bot Token"
                        value={telegramCredentials.bot_token}
                        onChange={(e) => setTelegramCredentials(prev => ({...prev, bot_token: e.target.value}))}
                        className="h-7 text-xs bg-[#030508] border-[#1a2540] focus:border-purple-500"
                        data-testid="telegram-bot-token-input"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] text-[#6b7a99] block mb-1">Chat ID</label>
                      <Input 
                        type="text"
                        placeholder="Your Telegram Chat ID"
                        value={telegramCredentials.chat_id}
                        onChange={(e) => setTelegramCredentials(prev => ({...prev, chat_id: e.target.value}))}
                        className="h-7 text-xs bg-[#030508] border-[#1a2540] focus:border-purple-500"
                        data-testid="telegram-chat-id-input"
                      />
                    </div>
                    <p className="text-[8px] text-purple-300/70">Get alerts on login and important activities on Telegram</p>
                  </div>
                )}
              </div>
              
              {/* Connect Button */}
              <Button 
                size="sm"
                onClick={() => connectWithCredentials('zerodha')}
                disabled={saving || connecting || !zerodhaCredentials.api_key || !zerodhaCredentials.api_secret}
                className="w-full h-9 text-xs bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                data-testid="zerodha-connect-btn"
              >
                {saving || connecting ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Connecting...</>
                ) : (
                  <><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Save & Login with Zerodha</>
                )}
              </Button>
              
              <p className="text-[8px] text-[#6b7a99] text-center">
                You'll be redirected to Zerodha's secure login page
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5 mb-3">
                <div className="flex items-center gap-1.5 text-[10px] text-[#a0aec0]">
                  <BarChart3 className="w-3 h-3 text-cyan-400" />
                  5-level Market Depth
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#a0aec0]">
                  <Activity className="w-3 h-3 text-cyan-400" />
                  Real-time Tick Data
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-orange-400">
                  <LogOut className="w-3 h-3" />
                  Daily Login (24hr token)
                </div>
              </div>
              
              <Button 
                variant="outline"
                size="sm"
                onClick={() => handleBrokerSelect('zerodha')}
                className="w-full h-8 text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                data-testid="select-zerodha-btn"
              >
                {brokerStatus.zerodha.connected ? 'Continue' : 'Connect'}
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </>
          )}
        </div>
        
        {/* Info Box */}
        <div className="bg-[#0a1628]/50 border border-[#1a2540] rounded-lg p-2 text-center">
          <p className="text-[10px] text-[#6b7a99]">
            Credentials encrypted securely. Click <Edit2 className="w-2.5 h-2.5 inline mx-0.5" /> to edit credentials.
          </p>
        </div>
        
        {/* Logout Link */}
        <div className="text-center mt-4">
          <button
            onClick={handleLogout}
            className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 mx-auto"
            data-testid="logout-btn"
          >
            <LogOut className="w-3 h-3" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrokerSelector;
